import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  School,
  LocationOn,
  People,
  Subscriptions,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { fetchSchools, createSchool, updateSchoolAsync, deleteSchoolAsync } from '../../store/slices/schoolSlice';
import { School as SchoolType } from '../../types';
import { saveAs } from 'file-saver';
import schoolService from '../../services/schoolService';
import studentsService from '../../services/studentService';
import { useNavigate } from 'react-router-dom';


const SchoolsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { schools, isLoading, error } = useSelector((state: RootState) => state.schools);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSchool, setEditingSchool] = useState<SchoolType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
  });
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [studentCounts, setStudentCounts] = useState<{ [schoolId: string]: number }>({});
  const [subscriptionCounts, setSubscriptionCounts] = useState<{ [schoolId: string]: number }>({});
  const countsFetched = useRef(false);

  useEffect(() => {
    dispatch(fetchSchools());
  }, [dispatch]);

  useEffect(() => {
    if (schools.length === 0 || countsFetched.current) return;
    countsFetched.current = true;

    const fetchCounts = async () => {
      const sc: { [schoolId: string]: number } = {};
      const sub: { [schoolId: string]: number } = {};
      await Promise.all(
        schools.map(async (school) => {
          try {
            const res = await studentsService.getStudentsBySchool(school.id);
            sc[school.id] = res.data.length;
            sub[school.id] = res.data.reduce((total: number, student: any) => {
              return total + (student.subjects?.length || 0);
            }, 0);
          } catch {
            sc[school.id] = 0;
            sub[school.id] = 0;
          }
        })
      );
      setStudentCounts(sc);
      setSubscriptionCounts(sub);
    };
    fetchCounts();
  }, [schools]);

  const handleOpenDialog = (school?: SchoolType) => {
    if (school) {
      setEditingSchool(school);
      setFormData({
        name: school.name,
        address: school.address || '',
        city: school.city || '',
        state: school.state || '',
        country: school.country || '',
        pincode: school.pincode?.toString() || '',
      });
    } else {
      setEditingSchool(null);
      setFormData({
        name: '',
        address: '',
        city: '',
        state: '',
        country: '',
        pincode: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSchool(null);
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      country: '',
      pincode: '',
    });
  };

  const handleSubmit = async () => {
    try {
      const schoolData = {
        ...formData,
        pincode: formData.pincode ? parseInt(formData.pincode) : undefined
      };

      if (editingSchool) {
        await dispatch(updateSchoolAsync({
          id: editingSchool.id,
          schoolData: schoolData
        })).unwrap();
      } else {
        await dispatch(createSchool(schoolData)).unwrap();
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save school:', error);
      // Error is already handled by the slice
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this school?')) {
      try {
        await dispatch(deleteSchoolAsync(id)).unwrap();
      } catch (error) {
        console.error('Failed to delete school:', error);
        // Error is already handled by the slice
      }
    }
  };

  const handleExportSchools = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const blob = await schoolService.exportSchools();
      const timestamp = new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14);
      saveAs(blob, `schools_export_${timestamp}.xlsx`);
    } catch (err: any) {
      setExportError(err.message || 'Failed to export schools');
    } finally {
      setExporting(false);
    }
  };



  const filteredSchools = schools.filter(school =>
    school.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (school.city && school.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (school.state && school.state.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Loading schools...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            LiveOlympiad Schools
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage all registered schools and their information
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleExportSchools}
            disabled={exporting}
            sx={{ minWidth: 140 }}
          >
            {exporting ? 'Exporting...' : 'Export Schools'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
            }}
          >
            Add School
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }}>{exportError}</Alert>
      )}

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search schools by name, city, or state..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Schools Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
        {filteredSchools.map((school) => (
          <Box key={school.id}>
            <Card 
              sx={{ 
                height: 280,
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* School Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      backgroundColor: 'primary.main',
                      mr: 2,
                      width: 48,
                      height: 48,
                    }}
                  >
                    <School />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {school.name?.split(',')[0] || school.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Code: {school.schoolcode || 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                {/* Location */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationOn sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {school.name?.includes(',') ? school.name.split(',')[1]?.trim() : (school.city && school.state ? `${school.city}, ${school.state}` : 'N/A')}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                      }}
                    >
                      {school.address || 'No address provided'}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                {/* Stats */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ textAlign: 'center', flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                      <People sx={{ fontSize: 16, color: 'primary.main', mr: 0.5 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {studentCounts[school.id] !== undefined ? studentCounts[school.id] : '-'}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Students
                    </Typography>
                  </Box>
                                       <Box sx={{ textAlign: 'center', flex: 1 }}>
                       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                         <Subscriptions sx={{ fontSize: 16, color: 'secondary.main', mr: 0.5 }} />
                         <Typography variant="h6" sx={{ fontWeight: 600 }}>
                           {subscriptionCounts[school.id] !== undefined ? subscriptionCounts[school.id] : '-'}
                         </Typography>
                       </Box>
                       <Typography variant="caption" color="text.secondary">
                         Subscriptions
                       </Typography>
                     </Box>
                </Box>



                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    onClick={() => navigate(`/schools/${school.id}/students`)}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    View Students
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(school)}
                    sx={{ color: 'primary.main' }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(school.id)}
                    sx={{ color: 'error.main' }}
                  >
                    <Delete />
                  </IconButton>
                </Box>
                             </CardContent>
             </Card>
           </Box>
         ))}
       </Box>

      {/* Empty State */}
      {filteredSchools.length === 0 && (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <CardContent>
            <School sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No schools found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm ? 'Try adjusting your search criteria' : 'Get started by adding your first school'}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit School Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSchool ? 'Edit School' : 'Add New School'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
            <Box sx={{ flex: '1 1 100%', minWidth: 0 }}>
              <TextField
                fullWidth
                label="School Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 100%', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Pin Code"
                value={formData.pincode}
                onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
                helperText="6-digit pin code"
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
            }}
          >
            {editingSchool ? 'Update' : 'Add'} School
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchoolsPage; 